import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRight from "@mui/icons-material/ChevronRight";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import { SidebarMain } from "./style";
import {
  CircularProgress,
  Collapse,
  Input,
  Grid,
} from "@mui/material";
import { Link, useLocation, useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import AccountMenu from "../AccountMenu/AccountMenu";
import axios from "axios";
import { configuration } from "../../helper/appconfig";
import { connect } from "react-redux";
import { handleAuthTokenError } from "../../redux/actions/authActions";
import toastAlert from "../../helper/ToastAlert/ToastAlert";
import { DarkButton } from "../../common-styled";
import CompanyLogo from "../../pages/CompanyLogo/CompanyLogo";
import { sidebarLinks } from "./sidebarLinks";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  [theme.breakpoints.down("md")]: {
    display: "block",
    position: "fixed",
    zIndex: "99999999",
    width: "40vw",
  },
  [theme.breakpoints.down("sm")]: {
    width: "80vw",
  },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open
    ? {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down("md")]: {
          width: "100% ",
        },
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }
    : {
        width: `calc(100% - ${theme.spacing(8)} + 1px)`,
        [theme.breakpoints.down("md")]: {
          width: "100% ",
        },
      }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const NewSidebarOptimized = ({ authData, children, handleAuthTokenError }) => {
  const [open, setOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [searchString, setSearchString] = useState("");
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const location = useLocation();
  const history = useHistory();

  const handleDrawerOpen = () => setOpen(true);
  
  const handleDrawerClose = () => {
    setOpen(false);
    setOpenMenus({});
  };

  const toggleMenu = (menuId) => {
    if (!open) return;
    setOpenMenus((prev) => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [menuId]: !prev[menuId],
    }));
  };

  const handleSearchTextChange = (evt) => {
    setSearchString(evt?.target?.value);
    setError([]);
  };

  const searchLabs = () => {
    const url = configuration.searchLabs;
    const tokenType = authData?.tokenType;
    const idToken = authData?.idToken;
    const headers = {
      Authorization: `${tokenType} ${idToken}`,
    };
    const body = {
      search: searchString,
    };
    
    axios
      .post(url, body, { headers })
      .then((response) => {
        setLoading(false);
        if (response?.data?.length) {
          history.push({
            pathname: "/filteredLabs",
            state: { filteredLabData: response.data },
          });
        } else {
          toastAlert("No labs found with this title", "success");
        }
      })
      .catch((error) => {
        setLoading(false);
        let errorMessage = error?.response?.data?.message;
        if (errorMessage === "Unauthorized") {
          handleAuthTokenError({ authData, history });
        }
        toastAlert(
          errorMessage || "Something went wrong. Please try again later.",
          "error"
        );
      });
  };

  const handleSearch = () => {
    if (!searchString.trim()) {
      setError(["searchStringEmpty"]);
    } else {
      setError([]);
      setLoading(true);
      searchLabs();
    }
  };

  const handleOnKeyDown = (event) => {
    if (event.keyCode === 13) {
      handleSearch();
    }
  };

  const getAppBarTitle = () => {
    const pathName = location?.pathname;
    const titleMap = {
      "/dashboard": "Dashboard",
      "/userProfile": "User Profile",
      "/AllUser": "All Users",
      "/createuser": "Create User",
      "/createlab": "Create Lab",
      "/alllabs": "All Labs List",
      "/filteredLabs": "Filtered Labs",
      "/allPolicies": "All Policy List",
      "/allAttachedPolicies": "All Attached Policy List",
      "/createcategory": "Create Category",
      "/category/AllCategory": "All Category",
      "/level/AllLevels": "All Level",
      "/allUserGroups": "All User Groups",
      "/level/CreateLevels": "Create Level",
      "/allGroups": "All Group",
      "/attachPolicy": "Attach Policy",
      "/creategroup": "Create Group",
      "/createpolicy": "Create Policy",
      "/createUserGroup": "Create User Group",
      "/myCredit": "My Credits",
      "/Transactions": "Transactions",
      "/Payment": "Payment",
      "/labProvisionHistory": "Lab Provision History",
      "/myGroups": "My Groups",
      "/myAttachedPolicies": "My Attached Policies",
      "/generateCoupon": "Generate Coupon",
      "/couponList": "All Coupons",
      "/gradingHistory": "Grading History",
      "/certificate": "Certificates",
      "/certificateList": "Certificate List",
    };

    if (titleMap[pathName]) return titleMap[pathName];
    if (pathName.includes("/viewlab/")) return "View Lab";
    if (pathName.includes("/editlab/")) return "Edit Lab";
    if (pathName.includes("/editcategory/")) return "Edit Category";
    if (pathName.includes("/editlevel/")) return "Edit Level";
    if (pathName.includes("/editGroup/")) return "Edit Group";
    if (pathName.includes("/editUserGroup/")) return "Edit User Group";
    if (pathName.includes("/editAttachedPolicy/")) return "Edit Attached Policy";
    if (pathName.includes("/viewPolicy/")) return "View Policy";
    if (pathName.includes("/editpolicy/")) return "Edit Policy";
    if (pathName.includes("/groupUsers/")) return "Group Users";
    if (pathName.includes("/monitoring/live-user-list")) return "Live User List";
    if (pathName.includes("/view-ratings/")) return "View Rating";
    if (pathName.includes("/support")) return "Support";
    if (pathName.startsWith("/couponList/")) return "All Users of Coupon";
    if (pathName.includes("/alllabs/")) return location?.state?.labGroup;
    if (pathName.includes("/labGroup/")) return location?.state?.labGroup;
    if (pathName.includes("/groupPolicies/")) return location?.state?.groupName;
    
    return "";
  };

  const shouldShowSearch = () => {
    const pathName = location?.pathname;
    return (
      pathName === "/dashboard" ||
      pathName === "/filteredLabs" ||
      pathName.includes("/labGroup/") ||
      pathName.includes("/viewlab/")
    );
  };

  const shouldShowMenuItem = (item) => {
    const userRole = Cookies.get("userRole");
    
    // Check role permission
    if (!item.roles.includes(userRole)) return false;
    
    // Check additional conditions if any
    if (item.condition && !item.condition(Cookies)) return false;
    
    return true;
  };

  const renderMenuItem = (item) => {
    if (!shouldShowMenuItem(item)) return null;

    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const isOpen = openMenus[item.id] || false;

    if (!hasSubMenu && item.path) {
      // Simple menu item with direct link
      return (
        <ListItem key={item.id} className="sidebar-list-items">
          <ListItemIcon>{item.icon}</ListItemIcon>
          <Link to={{ pathname: item.path }}>
            <ListItemText primary={item.name} />
          </Link>
        </ListItem>
      );
    }

    if (hasSubMenu) {
      // Menu item with submenu
      const userRole = Cookies.get("userRole");
      const filteredSubMenu = item.subMenu.filter((subItem) =>
        subItem.roles.includes(userRole)
      );

      if (filteredSubMenu.length === 0) return null;

      return (
        <React.Fragment key={item.id}>
          <ListItem
            className="sidebar-list-items policy-spacing"
            onClick={() => toggleMenu(item.id)}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.name}
              style={{
                whiteSpace: "break-spaces",
                padding: "0px 16px !important",
              }}
            />
            {isOpen ? <ExpandMoreIcon /> : <ChevronRight />}
          </ListItem>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {filteredSubMenu.map((subItem) => (
              <ListItem key={`${item.id}-${subItem.id}`}>
                <ListItemIcon />
                <Link to={subItem.path}>
                  <ListItemText primary={subItem.name} className="Link_item" />
                </Link>
              </ListItem>
            ))}
          </Collapse>
        </React.Fragment>
      );
    }

    return null;
  };

  return (
    <Grid container>
      <Grid item lg={12} md={12} sm={12} xs={12}>
        <SidebarMain>
          <Box sx={{ display: "flex" }} className="demo_class">
            <CssBaseline />
            {/* AppBar */}
            <AppBar className="appbarmain" position="fixed" open={open}>
              <Toolbar style={{ alignItems: "center" }}>
                <Grid container style={{ alignItems: "center" }}>
                  <Grid item lg={6} sm={3} md={5} xs={5}>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={open ? handleDrawerClose : handleDrawerOpen}
                        edge="start"
                        className="menuIcon"
                      >
                        {open ? <ChevronLeftIcon /> : <MenuIcon className="menu" />}
                      </IconButton>
                      <Typography
                        className="appbarTitle"
                        variant="h6"
                        noWrap
                        component="div"
                      >
                        {getAppBarTitle()}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid
                    item
                    lg={6}
                    sm={9}
                    md={7}
                    xs={7}
                    className="app_bar_right_content"
                  >
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {shouldShowSearch() && (
                        <div>
                          <div
                            style={{
                              display: "flex",
                              width: "100%",
                              alignItems: "center",
                              marginTop: error?.length && "12px",
                            }}
                          >
                            <div style={{ width: "100%" }}>
                              <Input
                                style={{
                                  borderRadius: "5px 0px 0px 5px",
                                  padding: "2.3px 5px",
                                  background: "white",
                                  border: "1px solid black",
                                }}
                                sx={{
                                  display: {
                                    xs: "block  !important ",
                                    sm: "block !important",
                                    md: "block !important",
                                    lg: "block !important",
                                  },
                                }}
                                className="search"
                                onChange={handleSearchTextChange}
                                fullWidth
                                placeholder="Quick Search for Labs..."
                                size="small"
                                type="search"
                                onKeyDown={handleOnKeyDown}
                              />
                            </div>
                            <div>
                              <DarkButton
                                style={{
                                  borderRadius: "0px 5px 5px 0px",
                                  minWidth: "45px",
                                }}
                                onClick={handleSearch}
                              >
                                {loading ? (
                                  <CircularProgress sx={{ color: "#fff" }} size={20} />
                                ) : (
                                  <SearchIcon />
                                )}
                              </DarkButton>
                            </div>
                          </div>
                          <div>
                            {error.includes("searchStringEmpty") && (
                              <span style={{ color: "red" }}>
                                kindly enter lab value!
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div
                        sx={{ display: { xs: "unset  !important" } }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: "20px",
                        }}
                      >
                        <div>
                          <Typography
                            sx={{
                              display: {
                                xs: "none !important ",
                                sm: "block !important",
                                md: "block!important",
                                lg: "block !important",
                              },
                            }}
                            className="admin-title"
                            variant="h6"
                          >
                            {(authData?.firstName || Cookies.get("firstName")) +
                              " " +
                              (authData?.lastName || Cookies.get("lastName"))}
                          </Typography>
                          <Typography
                            sx={{
                              display: {
                                xs: "none !important ",
                                sm: "block !important",
                                md: "block !important",
                                lg: "block !important",
                              },
                            }}
                            variant="p"
                            className="small-text"
                          >
                            {authData?.userEmail || Cookies.get("email")}
                          </Typography>
                        </div>
                        <AccountMenu />
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Toolbar>
            </AppBar>
            {/* Drawer */}
            <Drawer
              className="drawerMain"
              variant="permanent"
              open={open}
              onClick={open ? () => "" : handleDrawerOpen}
            >
              <DrawerHeader className="drawerheader">
                {open ? (
                  <CompanyLogo />
                ) : (
                  <img
                    src={authData.logo}
                    alt="logo"
                    className="logo_close logo-sidebar"
                  />
                )}
                <IconButton
                  sx={{
                    display: {
                      xs: "block ",
                      sm: "block",
                      md: "none !important",
                      lg: "none !important",
                    },
                  }}
                  onClick={handleDrawerClose}
                >
                  <HighlightOffIcon />
                </IconButton>
              </DrawerHeader>
              {/* Render sidebar links dynamically */}
              {sidebarLinks.map((item) => renderMenuItem(item))}
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
              <DrawerHeader />
              {children}
            </Box>
          </Box>
        </SidebarMain>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => ({
  authData: state.authReducer,
});

export default connect(mapStateToProps, { handleAuthTokenError })(
  NewSidebarOptimized
);
